import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AddressElement } from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';

import { SepaCardIcon } from '@mopla/ui';

import { IBankAccount } from '../../types';

const SepaCard = styled(Box)(({ theme }) => ({
	border: `1px solid ${theme.palette.primary.main}`,
	borderRadius: 8,
	padding: '9px 12px',
	height: 65,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08), 0px 3px 6px rgba(0, 0, 0, 0.04)',
}));

const FieldLabel = styled(Typography)({
	fontSize: 16,
	lineHeight: '22px',
	letterSpacing: 0.3,
	color: '#66758f',
	fontFamily: 'Palanquin, sans-serif',
});

const FieldInput = styled('input')(({ theme }) => ({
	display: 'block',
	width: '100%',
	height: 50,
	padding: '9px 12px',
	backgroundColor: '#fff',
	borderRadius: 8,
	fontSize: 18,
	lineHeight: '22px',
	color: theme.palette.primary.main,
	border: '1px solid #66758f',
	boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.08), 0px 1px 1px rgba(0, 0, 0, 0.04)',
	boxSizing: 'border-box',
	outline: 'none',
	fontFamily: 'Palanquin, sans-serif',
	'&:disabled': {
		color: '#454e5e',
		cursor: 'not-allowed',
	},
}));

interface IProps {
	bankAccount: IBankAccount;
	hasEmailValidationError: boolean;
	hasAddressValidationError: boolean;
	handleOnEmailChange: (e: { target: { value: string } }) => void;
	handleOnAddressChange: (event: StripeAddressElementChangeEvent) => void;
}

export const CheckoutStep: FC<IProps> = ({
	bankAccount,
	handleOnEmailChange,
	handleOnAddressChange,
	hasEmailValidationError,
	hasAddressValidationError,
}) => {
	const { t } = useTranslation('openBanking');

	return (
		<Stack gap="16px" sx={{ mt: 2 }}>
			<SepaCard>
				<SepaCardIcon />
				<Typography
					variant="h5-sb"
					color="primary.main"
					sx={{ lineHeight: '22px' }}
				>
					{t('checkout.sepa_debit')}
				</Typography>
			</SepaCard>

			<Stack gap="4px">
				<Stack gap="8px">
					<FieldLabel>{t('checkout.label_iban')}</FieldLabel>
					<FieldInput type="text" value={bankAccount.iban} disabled />
				</Stack>

				<Stack gap="8px">
					<FieldLabel>{t('checkout.label_email')}</FieldLabel>
					<FieldInput
						type="email"
						value={bankAccount.email}
						onChange={handleOnEmailChange}
						autoComplete="email"
					/>
					{hasEmailValidationError && (
						<Typography variant="body2" color="error">
							{t('not_valid_email')}
						</Typography>
					)}
				</Stack>
			</Stack>

			<Stack gap="4px">
				<AddressElement
					options={{
						mode: 'billing',
						defaultValues: {
							name: bankAccount.name,
							address: bankAccount.address,
						},
					}}
					onChange={handleOnAddressChange}
				/>
				{hasAddressValidationError && (
					<Typography variant="body2" color="error">
						{t('not_valid_address')}
					</Typography>
				)}
			</Stack>

			<Typography
				sx={{
					fontSize: 14,
					lineHeight: '18px',
					color: '#666',
				}}
			>
				{/* Taken from Stripe */}
				{t('sepa_agreement')}
			</Typography>
		</Stack>
	);
};
